import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  InputAdornment,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  GetAllModelTypeArray,
  GetPartDetailsByIdResponse,
  PartFamily,
  RootPartDto,
  TransferOrderPartDetails,
} from "../../../@types/DRO/GetPartsListResponse";
import {
  PartTier,
  UpdatePartsBody,
} from "../../../@types/DTO/AddPartsBody";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import {
  AppAutoCompleteWithSearchAPI,
  OptionType,
} from "../../Layouts/AppAutoCompleteWithSearchAPI/AppAutoCompleteWithSearchAPI";
import RoleGuard from "../../auth/RoleGuard";

const parseBooleanFlag = (value: unknown): boolean | undefined => {
  if (value === true || value === 1 || value === "1" || value === "true") {
    return true;
  }
  if (value === false || value === 0 || value === "0" || value === "false") {
    return false;
  }
  return undefined;
};

const extractPartFromDetailResponse = (
  response: GetPartDetailsByIdResponse
): GetPartDetailsByIdResponse["data"] & Record<string, unknown> => {
  const payload = response as unknown as Record<string, unknown>;
  const nested = payload?.data as Record<string, unknown> | undefined;
  const deeplyNested = nested?.data as Record<string, unknown> | undefined;

  if (deeplyNested?.id != null) {
    return deeplyNested as GetPartDetailsByIdResponse["data"] &
      Record<string, unknown>;
  }

  if (nested?.id != null) {
    return nested as GetPartDetailsByIdResponse["data"] & Record<string, unknown>;
  }

  if (payload?.id != null) {
    return payload as GetPartDetailsByIdResponse["data"] & Record<string, unknown>;
  }

  return (nested ?? {}) as GetPartDetailsByIdResponse["data"] &
    Record<string, unknown>;
};

const resolveIsSpecialPart = (
  part: Record<string, unknown>,
  navigationValue: unknown
): boolean =>
  parseBooleanFlag(part.isSpecialPart) ??
  parseBooleanFlag(part.specialPart) ??
  parseBooleanFlag(navigationValue) ??
  false;

interface PartDetails {
  id: number;
  externalId: number;
  description: string;
  costPrice: number;
  salesPrice: number;
  hsn: string;
  quantity: number;
  productType: string;
  status: number;
  vendorType: string;
  businessType: string;
  elementType: string;
  oemType: string;
  tax: number;
  penaltyWaived?: boolean;
  isSpecialPart?: boolean;
  partTier?: PartTier | null;
  rootPartDto?: RootPartDto;
  partFamily?: PartFamily;
}

type PartDetailsNavigationState = {
  isSpecialPart?: unknown;
};

const PartDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationState = location.state as PartDetailsNavigationState | null;
  const [partDetails, setPartDetails] = useState<PartDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info",
  });
  // Editable fields state
  const [costPrice, setCostPrice] = useState<string>("");
  const [salesPrice, setSalesPrice] = useState<string>("");
  const [tax, setTax] = useState<string>("");
  const [partTier, setPartTier] = useState<PartTier | "">("");
  const [description, setDescription] = useState<string>("");
  const [descriptionError, setDescriptionError] = useState<string>("");
  const [penaltyWaived, setPenaltyWaived] = useState<boolean>(true);
  const [partTypes, setPartTypes] = useState<GetAllModelTypeArray[]>([]);
  const [selectedPartType, setSelectedPartType] =
    useState<GetAllModelTypeArray | null>(null);
  const [isSpecialPart, setIsSpecialPart] = useState<boolean>(() =>
    resolveIsSpecialPart({}, navigationState?.isSpecialPart)
  );

  // Root Part autocomplete state
  const [selectedPartValue, setSelectedPartValue] =
    useState<OptionType<TransferOrderPartDetails> | null>(null);
  const [selectedPartIds, setSelectedPartIds] = useState<Set<number>>(
    new Set()
  );
  const [selectedOEMTypeId] = useState<number | null>(null);
  const [rowsPerPage] = useState(10);

  const userAuthority = localStorage.getItem("authority");
  const isInventoryManager = userAuthority === "ROLE_INVENTORY_MANAGER";

  useEffect(() => {
    const fetchPartDetails = async () => {
      try {
        setLoading(true);
        const [response, partTypeResponse] = await Promise.all([
          AllPartApi.api.getPartsDetailById(Number(id)),
          AllPartApi.api.getPartType(),
        ]);
        const part = extractPartFromDetailResponse(response);
        setPartTypes(partTypeResponse.data);
        setPartDetails(part as PartDetails);
        setError(null);
        // Set editable fields
        setCostPrice(part.costPrice?.toString() || "");
        setSalesPrice(part.salesPrice?.toString() || "");
        setTax(part.tax?.toString() || "");
        setPartTier(
          part.partTier === "HIGH" ||
            part.partTier === "MEDIUM" ||
            part.partTier === "LOW"
            ? part.partTier
            : ""
        );
        setDescription(part.description || "");
        setPenaltyWaived(part.penaltyWaived ?? true);
        setIsSpecialPart(
          resolveIsSpecialPart(part, navigationState?.isSpecialPart)
        );
        setSelectedPartType(
          partTypeResponse.data.find(
            (type) => type.description === part.elementType
          ) ?? null
        );

        // Set root part if exists
        if (part.rootPartDto && part.rootPartDto.partId) {
          setSelectedPartValue({
            value: part.rootPartDto.partId.toString(),
            label: part.rootPartDto.partName,
            data: {
              id: part.rootPartDto.partId,
              description: part.rootPartDto.partName,
            } as TransferOrderPartDetails,
          });
          setSelectedPartIds(new Set([part.rootPartDto.partId]));
        }
      } catch (err) {
        console.error("Error fetching part details:", err);
        setError("Failed to load part details");
        setSnackbar({
          open: true,
          message: "Failed to load part details",
          severity: "error",
        });
        setPartDetails(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPartDetails();
    }
  }, [id, navigationState?.isSpecialPart]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const fetchPaginatedPartsBySearchTerm = useCallback(
    (
      currentPageNumber: number,
      currentSearchQueryTerm: string
    ): Promise<{
      options: OptionType<TransferOrderPartDetails>[];
      hasMore: boolean;
    }> => {
      const oemTypeIdToUse = selectedOEMTypeId;

      const fetchMultiplier = Math.max(
        2,
        Math.ceil(selectedPartIds.size / rowsPerPage) + 1
      );
      const adjustedPageSize = rowsPerPage * fetchMultiplier;

      return AllPartApi.api
        .getPartList(
          currentPageNumber,
          adjustedPageSize,
          currentSearchQueryTerm,
          undefined,
          undefined,
          oemTypeIdToUse || undefined
        )
        .then(({ data: response }) => {
          const availableParts = response.data.filter(
            (part) =>
              !selectedPartIds.has(part.id) ||
              part.id === selectedPartValue?.data?.id
          );

          return {
            options: availableParts?.map((part) => ({
              value: part.id.toString(),
              label: part.description,
              data: part,
            })),
            hasMore:
              currentPageNumber < response.totalPages - 1 ||
              availableParts.length >= rowsPerPage,
          };
        })
        .catch(() => ({ options: [], hasMore: false }));
    },
    [selectedOEMTypeId, selectedPartIds, rowsPerPage, selectedPartValue]
  );

  const handlePartSelect = (
    event: any,
    value: OptionType<TransferOrderPartDetails> | null
  ) => {
    const oldId = selectedPartValue?.data?.id;
    const newId = value?.data?.id;

    if (oldId && oldId > 0) {
      setSelectedPartIds((prev) => {
        const next = new Set(prev);
        next.delete(oldId);
        return next;
      });
    }

    if (newId && newId > 0) {
      setSelectedPartIds((prev) => new Set(prev).add(newId));
    }

    setSelectedPartValue(value);
  };

  const handlePartTypeSelect = (
    event: React.SyntheticEvent<Element, Event>,
    value: GetAllModelTypeArray | null
  ) => {
    setSelectedPartType(value);
  };

  if (loading) {
    return (
      <Box sx={{ padding: 4 }}>
        <Typography variant="h5" gutterBottom>
          Loading part details...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontWeight: "600",
            color: "#1E1E1E",
            textAlign: "left",
            fontSize: "1.5rem",
            mb: 1,
          }}>
          Part Details
        </Typography>
        <div className="flex items-center bg-gray-100 px-4 py-2 rounded-md space-x-2">
          <Link
            to="/home"
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
            Dashboard
          </Link>
          <span className="text-sm text-gray-400">/</span>
          <Link
            to="/product-template"
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
            Parts List
          </Link>
          <span className="text-sm text-gray-400">/</span>
          <span className="text-sm font-medium text-gray-700">
            Part Details
          </span>
        </div>
      </Box>

      {error ? (
        <Paper
          sx={{
            padding: 3,
            marginTop: 2,
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          }}>
          <Typography color="error">{error}</Typography>
          <Box sx={{ marginTop: 2 }}>
            <Button
              onClick={() => navigate(-1)}
              variant="contained"
              sx={{
                padding: "10px 20px",
                borderRadius: "32px",
                backgroundColor: "#1E1E1E",
                textTransform: "none",
                fontWeight: "500",
                "&:hover": { backgroundColor: "#000000" },
              }}>
              Back to List
            </Button>
          </Box>
        </Paper>
      ) : partDetails ? (
        <>
          <Paper
            sx={{
              padding: 3,
              marginTop: 2,
              borderRadius: "8px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            }}>
            <Grid container spacing={3} sx={{ textAlign: "left" }}>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  ID
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  value={partDetails.id}
                  variant="standard"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  External ID
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  value={partDetails.externalId}
                  variant="standard"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Description
                </Typography>
                <TextField
                  fullWidth
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    if (descriptionError && e.target.value.trim()) {
                      setDescriptionError("");
                    }
                  }}
                  variant="standard"
                  type="text"
                  required
                  error={!!descriptionError}
                  helperText={descriptionError}
                  disabled={isInventoryManager}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Root Part (Optional)
                </Typography>
                <AppAutoCompleteWithSearchAPI
                  key={`product-select-oem-${selectedOEMTypeId || "none"}`}
                  onChange={handlePartSelect}
                  initialPageNumber={0}
                  value={selectedPartValue}
                  disabled={isInventoryManager}
                  getData={function (data: {
                    currentPageNumber: number;
                    currentSearchQueryTerm: string;
                  }): Promise<{
                    options: OptionType<TransferOrderPartDetails>[];
                    hasMore: boolean;
                  }> {
                    return fetchPaginatedPartsBySearchTerm(
                      data.currentPageNumber,
                      data.currentSearchQueryTerm
                    );
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Cost Price
                </Typography>
                <TextField
                  fullWidth
                  value={costPrice}
                  onChange={(e) =>
                    setCostPrice(e.target.value.replace(/[^\d.]/g, ""))
                  }
                  variant="standard"
                  type="text"
                  disabled={isInventoryManager}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Sales Price
                </Typography>
                <TextField
                  fullWidth
                  value={salesPrice}
                  onChange={(e) =>
                    setSalesPrice(e.target.value.replace(/[^\d.]/g, ""))
                  }
                  variant="standard"
                  type="text"
                  disabled={isInventoryManager}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  HSN Code
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  value={partDetails.hsn}
                  variant="standard"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Part Family
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  value={partDetails.partFamily?.partFamily ?? ""}
                  variant="standard"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Part Location
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  value={partDetails.partFamily?.partLocation ?? ""}
                  variant="standard"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              {/* <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Quantity
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  value={partDetails.quantity}
                  variant="standard"
                  InputProps={{ readOnly: true }}
                />
              </Grid> */}

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Product Type
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  value={partDetails.productType}
                  variant="standard"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Status
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  value={partDetails.status === 1 ? "Active" : "Inactive"}
                  variant="standard"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Vendor Type
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  value={partDetails.vendorType}
                  variant="standard"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Business Type
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  value={partDetails.businessType}
                  variant="standard"
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Element Type
                </Typography>
                <Autocomplete
                  options={partTypes}
                  getOptionLabel={(option) => option.description}
                  value={selectedPartType}
                  onChange={handlePartTypeSelect}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disabled={isInventoryManager}
                  renderInput={(params) => (
                    <TextField {...params} variant="standard" />
                  )}
                />
              </Grid>

              <RoleGuard deny={["ROLE_FRANCHISE_INVENTORY_MANAGER", "ROLE_ERP_FRANCHISE_ADMIN"]}>
                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    sx={{ fontWeight: "500", mb: 1 }}>
                    OEM Type
                  </Typography>
                  <TextField
                    fullWidth
                    disabled
                    value={partDetails.oemType}
                    variant="standard"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </RoleGuard>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Tax
                </Typography>
                <TextField
                  fullWidth
                  value={tax}
                  onChange={(e) =>
                    setTax(e.target.value.replace(/[^\d.]/g, ""))
                  }
                  variant="standard"
                  type="text"
                  disabled={isInventoryManager}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <RoleGuard deny={["ROLE_FRANCHISE_INVENTORY_MANAGER", "ROLE_ERP_FRANCHISE_ADMIN"]}>
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Special Part
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Switch
                    checked={Boolean(isSpecialPart)}
                    onChange={(e) => setIsSpecialPart(e.target.checked)}
                    color="primary"
                    disabled={isInventoryManager}
                  />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {isSpecialPart ? "True" : "False"}
                  </Typography>
                </Box>
              </Grid>
              {selectedPartType?.description === "Consumable" && (
                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    sx={{ fontWeight: "500", mb: 1 }}>
                    Special Part
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Switch
                      checked={Boolean(isSpecialPart)}
                      onChange={(e) => setIsSpecialPart(e.target.checked)}
                      color="primary"
                      disabled={isInventoryManager}
                    />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {isSpecialPart ? "True" : "False"}
                    </Typography>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  sx={{ fontWeight: "500", mb: 1 }}>
                  Part Tier *
                </Typography>
                <Autocomplete<PartTier>
                  options={["HIGH", "MEDIUM", "LOW"]}
                  value={partTier || null}
                  onChange={(_, value) => setPartTier(value || "")}
                  disabled={isInventoryManager}
                  renderInput={(params) => (
                    <TextField {...params} variant="standard" required />
                  )}
                />
              </Grid>
              </RoleGuard>
            </Grid>        
          </Paper>
          
          {/* Update Button */}
          {!isInventoryManager && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <RoleGuard deny={["ROLE_FRANCHISE_INVENTORY_MANAGER", "ROLE_ERP_FRANCHISE_ADMIN"]}>
              <Button
                variant="contained"
                sx={{
                  borderRadius: "32px",
                  textTransform: "none",
                  fontWeight: 500,
                  px: 4,
                  py: 1.5,
                  backgroundColor: "#1E1E1E",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#000000" },
                }}
                onClick={async () => {
                  if (!partDetails) return;

                  const trimmedDescription = description.trim();
                  if (!trimmedDescription) {
                    setDescriptionError("Description is required");
                    setSnackbar({
                      open: true,
                      message: "Description cannot be empty.",
                      severity: "error",
                    });
                    return;
                  }

                  const resolvedPartTypeId = selectedPartType?.id;
                  if (!resolvedPartTypeId) {
                    setSnackbar({
                      open: true,
                      message: "Please select a valid Element Type.",
                      severity: "error",
                    });
                    return;
                  }

                  if (!partTier) {
                    setSnackbar({
                      open: true,
                      message: "Please select a part tier.",
                      severity: "error",
                    });
                    return;
                  }

                  try {
                    const updatePayload: UpdatePartsBody = {
                      costPrice: parseFloat(costPrice),
                      salesPrice: parseFloat(salesPrice),
                      tax: parseFloat(tax),
                      partTypeId: resolvedPartTypeId,
                      partTier,
                      rootPartId: selectedPartValue?.data?.id,
                      description: trimmedDescription,
                      isSpecialPart,
                      ...(selectedPartType?.description === "Consumable"
                        ? { penaltyWaived }
                        : {}),
                      ...(partDetails.partFamily?.id != null
                        ? { partFamilyId: partDetails.partFamily.id }
                        : {}),
                    };

                    await AllPartApi.api.updatePartsInfo(
                      String(partDetails.id),
                      updatePayload
                    );
                    setDescriptionError("");
                    setSnackbar({
                      open: true,
                      message: "Part details updated successfully!",
                      severity: "success",
                    });
                    setTimeout(() => {
                      navigate("/product-template");
                    }, 2000);
                  } catch (err) {
                    setSnackbar({
                      open: true,
                      message: "Failed to update part details.",
                      severity: "error",
                    });
                  }
                }}>
                Update
              </Button>
              </RoleGuard>
            </Box>
          )}
        </>
      ) : (
        <Paper
          sx={{
            padding: 3,
            marginTop: 2,
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          }}>
          <Typography>No part details found</Typography>
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PartDetailsPage;
